/**
 * GF(256) arithmetic for QR Code Model 2 Reed–Solomon error correction, per
 * ISO/IEC 18004 Annex A: byte-wise modulo `x^8 + x^4 + x^3 + x^2 + 1`
 * (`0x11D`), generator α = 2. Verified against the spec's own worked
 * example (`qr-encode.test.ts`'s "Error Correction Coding" vector): the
 * 16-byte data-codeword sequence the spec derives for its "HELLO WORLD"
 * 1-M walkthrough produces the exact 10-byte ECC sequence the spec states.
 */

const PRIMITIVE_POLYNOMIAL = 0x11d
const FIELD_ORDER = 255 // GF(256) has 255 nonzero elements; the log table is mod this.

const EXP_TABLE = new Uint8Array(256) // EXP_TABLE[i] = 2^i in GF(256), i in [0, 254]
const LOG_TABLE = new Uint8Array(256) // LOG_TABLE[EXP_TABLE[i]] = i, for nonzero inputs only

function buildTables(): void {
	let value = 1
	for (let exponent = 0; exponent < FIELD_ORDER; exponent++) {
		EXP_TABLE[exponent] = value
		LOG_TABLE[value] = exponent
		value <<= 1
		if (value & 0x100) value ^= PRIMITIVE_POLYNOMIAL
	}
	EXP_TABLE[FIELD_ORDER] = EXP_TABLE[0] // convenience duplicate; unused by multiply (which mods explicitly)
}
buildTables()

/** Multiplies two GF(256) elements via the log/antilog tables. */
export function gfMultiply(a: number, b: number): number {
	if (a === 0 || b === 0) return 0
	return EXP_TABLE[(LOG_TABLE[a] + LOG_TABLE[b]) % FIELD_ORDER]
}

/**
 * Builds the Reed–Solomon generator polynomial for `degree` ECC codewords —
 * the product `(x - α^0)(x - α^1)...(x - α^(degree-1))`, coefficients
 * highest-degree-first, leading coefficient always 1. Subtraction is XOR in
 * GF(256), so `-α^i` is `α^i`.
 */
function buildGeneratorPolynomial(degree: number): number[] {
	let coeffs = [1]
	for (let i = 0; i < degree; i++) {
		const next = Array.from({ length: coeffs.length + 1 }, () => 0)
		const factor = EXP_TABLE[i]
		for (let j = 0; j < coeffs.length; j++) {
			next[j] ^= coeffs[j]
			next[j + 1] ^= gfMultiply(coeffs[j], factor)
		}
		coeffs = next
	}
	return coeffs
}

/**
 * Generates `eccCount` Reed–Solomon error-correction codewords for
 * `dataCodewords`, via the standard in-place polynomial long division: the
 * message polynomial (data codewords, padded with `eccCount` zero
 * low-order terms) divided by the generator polynomial; the remainder's
 * coefficients are the ECC codewords.
 */
export function rsGenerateEccCodewords(dataCodewords: readonly number[], eccCount: number): number[] {
	const generator = buildGeneratorPolynomial(eccCount)
	const remainder = [...dataCodewords, ...Array.from({ length: eccCount }, () => 0)]
	for (let i = 0; i < dataCodewords.length; i++) {
		const factor = remainder[i]
		if (factor === 0) continue
		for (let j = 0; j < generator.length; j++) {
			remainder[i + j] ^= gfMultiply(generator[j], factor)
		}
	}
	return remainder.slice(dataCodewords.length)
}

/**
 * Divides a BCH-coded value by `generator` (its bit pattern's numeric
 * value), stopping once the remainder is shorter than `generatorBitLength`
 * — the shared division this module's two BCH callers (format info,
 * §7.9.1; version info, §7.9.2) both need, differing only in generator and
 * field width.
 */
export function bchDivide(value: number, generator: number, generatorBitLength: number): number {
	let msb = value === 0 ? 0 : 32 - Math.clz32(value)
	while (msb >= generatorBitLength) {
		value ^= generator << (msb - generatorBitLength)
		msb = value === 0 ? 0 : 32 - Math.clz32(value)
	}
	return value
}
